/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
/* cspell: ignore ddfg, ddfgi */
import {
    ControlType,
    DropdownFormGroup,
    FieldType,
    ISettingGroupDefinition,
    Levels,
    RadioFormGroup,
    SettingGroupDefinition,
    StringFieldDefinition,
    StringResource
} from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionDropdownItem,
    IFieldDefinitionPanelOptionsData,
    IFieldDefinitionPanelOptionsProps,
    IFieldDefinitionsPanelData,
    IFieldDefinitionsPanelProps
} from "../Panels";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IDomainDataMapper } from "./IDomainDataMapper";
import { v4 as uuid } from "uuid";

const logger: ILogger = getLogger();

export abstract class DomainDataMapperOptionsBase implements IDomainDataMapper {
    private definition: ISettingGroupDefinition;

    constructor(settingDefinition: ISettingGroupDefinition) {
        this.definition = settingDefinition;
    }

    abstract RequiredControlTypeFunc(): ControlType.DropdownField | ControlType.RadioField;

    private GetDescription = (): string => {
        if (this.RequiredControlTypeFunc() === ControlType.RadioField) {
            return "Radio";
        } else if (this.RequiredControlTypeFunc() === ControlType.DropdownField) {
            return "Dropdown";
        } else {
            logger.logError("Unable to determine description from control type", {
                type: ControlType[this.RequiredControlTypeFunc()]
            });
            throw new Error("Unable to determine control type");
        }
    };

    /**
     * Field type expected by this mapper
     */
    readonly RequiredFieldType = FieldType.String;

    BuildPanelPropsForNewItem = (): IFieldDefinitionsPanelProps => {
        const defaultUuid = uuid();

        const result: IFieldDefinitionPanelOptionsProps = {
            common: {
                panelTitle: `Add ${this.GetDescription()}`,
                originalSettingName: "",
                isNew: true,
                isOpen: true
            },
            data: {
                definitionType: this.RequiredControlTypeFunc(),
                settingName: "",
                auditChanges: false,
                title: "",
                tooltip: "",
                helper: "",
                helpTextValid: true,
                defaultValue: "",
                accessLevels: Levels.Classic,
                visibleRule: "",
                enableRule: "",
                defaultKey: defaultUuid,
                // initialize the dropdown with 3 empty options
                dropDownItems: [
                    { key: defaultUuid, text: "", value: "" },
                    { key: uuid(), text: "", value: "" },
                    { key: uuid(), text: "", value: "" }
                ]
            }
        };

        return result;
    };

    /**
     *
     * @param ctrlGuid Control Guid
     * @returns Properties for a dropdown
     */
    BuildPanelPropsForExistingItem = (ctrlGuid: string): IFieldDefinitionsPanelProps => {
        const sg = new SettingGroupDefinition(this.definition);
        const [ddfg, sfd] = this.findControlAndFieldDefinition(sg, ctrlGuid);
        const settingName = ddfg.SettingName;
        const sr = sg.UI.StringResources;

        let defaultKey: string = "";

        const dropdownItems: IFieldDefinitionDropdownItem[] = ddfg.Items?.map(i => {
            const dropdownItem: IFieldDefinitionDropdownItem = {
                key: i.ItemGuid,
                value: i.Value,
                text: sr.get(StringResource.CreateMapKey(i.LabelStringId, "en-US"))?.Text ?? ""
            };

            if (sfd.DefaultValue === i.Value) {
                defaultKey = i.ItemGuid;
            }

            return dropdownItem;
        });

        const result: IFieldDefinitionPanelOptionsProps = {
            common: {
                panelTitle: `Edit ${this.GetDescription()}`,
                originalSettingName: settingName,
                isNew: false,
                isOpen: true
            },
            data: {
                ctrlGuid,
                definitionType: this.RequiredControlTypeFunc(),
                settingName: settingName,
                title:
                    sr.get(StringResource.CreateMapKey(ddfg.LabelTextStringId, "en-US"))?.Text ??
                    "",
                tooltip:
                    sr.get(StringResource.CreateMapKey(ddfg.TooltipStringId, "en-US"))?.Text ?? "",
                helper:
                    sr.get(StringResource.CreateMapKey(ddfg.HelperTextStringId, "en-US"))?.Text ??
                    "",
                helpTextValid: true,
                defaultValue: sfd.DefaultValue,
                defaultKey: defaultKey,
                accessLevels: sfd.AccessLevel,
                visibleRule: ddfg.VisibleRuleName,
                enableRule: ddfg.EnableRuleName,
                auditChanges: sfd.AuditChanges,
                dropDownItems: dropdownItems
            }
        };

        return result;
    };

    SaveDataToDefinition = (data: IFieldDefinitionsPanelData): ISettingGroupDefinition => {
        if (data.definitionType !== this.RequiredControlTypeFunc()) {
            logger.logError("Invalid control type", { type: ControlType[data.definitionType] });
            throw new Error("Invalid definition type.");
        }

        const dd = data as IFieldDefinitionPanelOptionsData;
        const sg = new SettingGroupDefinition(this.definition);

        if (dd.ctrlGuid === undefined) {
            this.addNewDropDownField(dd, sg);
        } else {
            this.updateExistingDropDownField(dd, sg);
        }

        return sg.toISettingGroupDefinition();
    };

    /**
     * Adds a new drop down field to the setting definition
     * @param dd Panel drop down data
     * @param sg Setting group definition
     */
    private addNewDropDownField(
        dd: IFieldDefinitionPanelOptionsData,
        sg: SettingGroupDefinition
    ): void {
        const sfd = new StringFieldDefinition(dd.settingName, dd.defaultValue);
        sfd.AccessLevel = dd.accessLevels;
        sfd.AuditChanges = dd.auditChanges;
        sg.AddFieldDefinition(sfd);

        let formGroup: DropdownFormGroup | RadioFormGroup;

        if (this.RequiredControlTypeFunc() === ControlType.DropdownField) {
            formGroup = sg.UI.AddDropdownFormGroup(dd.settingName);
        } else if (this.RequiredControlTypeFunc() === ControlType.RadioField) {
            formGroup = sg.UI.AddRadioFormGroup(dd.settingName);
        } else {
            //error
            logger.logError("Wrong control type", {
                type: ControlType[this.RequiredControlTypeFunc()]
            });
            throw new Error("Wrong control type");
        }

        formGroup.EnableRuleName = dd.enableRule;
        formGroup.VisibleRuleName = dd.visibleRule;

        if (!sg.UI.UpdateStringResourceText(formGroup.LabelTextStringId, dd.title)) {
            logger.logError("Error finding label string resource", {
                stringId: formGroup.LabelTextStringId
            });
            throw new Error("Error getting label string resource");
        }

        if (!sg.UI.UpdateStringResourceText(formGroup.TooltipStringId, dd.tooltip)) {
            logger.logError("Error finding tooltip string resource", {
                stringId: formGroup.TooltipStringId
            });
            throw new Error("Error getting tooltip string resource");
        }

        if (!sg.UI.UpdateStringResourceText(formGroup.HelperTextStringId, dd.helper)) {
            logger.logError("Error finding helper string resource", {
                stringId: formGroup.HelperTextStringId
            });
            throw new Error("Error getting helper string resource");
        }

        dd.dropDownItems?.forEach(item => {
            const si = sg.UI.AddSelectionItem(item.value, formGroup);

            if (!sg.UI.UpdateStringResourceText(si.LabelStringId, item.text)) {
                logger.logError("Error finding label string resource", {
                    stringId: si.LabelStringId
                });
                throw new Error("Error getting label string resource");
            }
        });
    }

    /**
     * Updates existing drop down field on the setting definition
     * @param dd Panel drop down data
     * @param sg Setting group definition
     */
    private updateExistingDropDownField(
        dd: IFieldDefinitionPanelOptionsData,
        sg: SettingGroupDefinition
    ): void {
        const [ddfg, sfd] = this.findControlAndFieldDefinition(sg, dd.ctrlGuid ?? "");
        sfd.DefaultValue = dd.defaultValue;
        sfd.AccessLevel = dd.accessLevels;
        sfd.AuditChanges = dd.auditChanges;
        ddfg.EnableRuleName = dd.enableRule;
        ddfg.VisibleRuleName = dd.visibleRule;

        if (!sg.UI.UpdateStringResourceText(ddfg.LabelTextStringId, dd.title)) {
            logger.logError("Error finding label string resource", {
                stringId: ddfg.LabelTextStringId
            });
            throw new Error("Error getting label string resource");
        }

        if (!sg.UI.UpdateStringResourceText(ddfg.TooltipStringId, dd.tooltip)) {
            logger.logError("Error finding tooltip string resource", {
                stringId: ddfg.TooltipStringId
            });
            throw new Error("Error getting tooltip string resource");
        }

        if (!sg.UI.UpdateStringResourceText(ddfg.HelperTextStringId, dd.helper)) {
            logger.logError("Error finding helper string resource", {
                stringId: ddfg.HelperTextStringId
            });
            throw new Error("Error getting helper string resource");
        }

        // do the cleanup of the ddfg first
        const idxToRemove: number[] = [];

        ddfg.Items?.forEach((i, idx) => {
            // if there is no corresponding entry in the panel
            // data, remove the entry from the dropdown form group.
            if (!dd.dropDownItems?.some(ddi => ddi.key === i.ItemGuid)) {
                idxToRemove.push(idx);
                // eslint-disable-next-line no-console
                console.log("added", idx);
            }
        });

        idxToRemove?.forEach(remove => {
            if (remove > -1) {
                // eslint-disable-next-line no-console
                console.log("remove", remove);
                ddfg.Items?.splice(remove, 1);
            }
        });

        dd.dropDownItems?.map(item => {
            // spin thru panel data...
            //
            // if item/option on panel and not on field group = add to field group
            // if item/option on panel and on field group = update field group
            //
            const key = item.key;
            const ddfgOption = ddfg.Items.find(ddfgi => ddfgi.ItemGuid === key);

            if (ddfgOption) {
                // found it so do the update.
                ddfgOption.Value = item.value;

                if (!sg.UI.UpdateStringResourceText(ddfgOption.LabelStringId, item.text)) {
                    logger.logError("Error finding label string resource", {
                        stringId: ddfgOption.LabelStringId
                    });
                    throw new Error("Error getting label string resource");
                }
            } else {
                // not found so it's an add
                const si = sg.UI.AddSelectionItem(item.value, ddfg);

                if (!sg.UI.UpdateStringResourceText(si.LabelStringId, item.text)) {
                    logger.logError("Error finding label string resource", {
                        stringId: si.LabelStringId
                    });
                    throw new Error("Error getting label string resource");
                }
            }
        });
    }

    /**
     * Find the control and field definition for a
     * control GUID
     * @param sg Setting group definition
     * @param ctrlGuid Control GUID
     * @returns TextFormGroup and StringFieldDefinition
     * associated with the control
     */
    private findControlAndFieldDefinition = (
        sg: SettingGroupDefinition,
        ctrlGuid: string
    ): [DropdownFormGroup | RadioFormGroup, StringFieldDefinition] => {
        const ctrlIndex = sg.UI.Controls.findIndex(i => i.CtrlGuid == ctrlGuid);
        if (ctrlIndex === -1) {
            logger.logError("Unable to find control", { ctrlGuid });
            throw new Error("Unable to find control.");
        }

        const uiCtrl = sg.UI.Controls[ctrlIndex];
        if (uiCtrl.ControlDiscriminator !== this.RequiredControlTypeFunc()) {
            logger.logError("Control type is not dropdown or radio", {
                type: ControlType[uiCtrl.ControlDiscriminator]
            });
            throw new Error(
                "Invalid control type.  Expecting DropdownField or RadioField control."
            );
        }

        let formGroup: DropdownFormGroup | RadioFormGroup; //
        if (this.RequiredControlTypeFunc() === ControlType.DropdownField) {
            formGroup = uiCtrl as DropdownFormGroup;
        } else if (this.RequiredControlTypeFunc() === ControlType.RadioField) {
            formGroup = uiCtrl as RadioFormGroup;
        } else {
            logger.logError("Bad control type", {
                type: ControlType[this.RequiredControlTypeFunc()]
            });
            throw new Error("Bad control type.");
        }

        const fd = sg.Fields.get(formGroup.SettingName);
        if (fd === undefined || fd.FieldDiscriminator !== this.RequiredFieldType) {
            logger.logError("Field type is not string", {
                type: fd?.FieldDiscriminator.toString()
            });
            throw new Error("Invalid field type.");
        }

        return [formGroup, fd as StringFieldDefinition];
    };
}
